import type { InventoryHealthVariant } from "./inventory_health_cell_context";
import { calculationFirstCellPrompt } from "./cell_explanation_prompt";
import type { CommonViewContextV1 } from "./common_view_context";
import { formatCurrency, isCurrencyField } from "../utils/currency_fields";
import { isNucleusExplainDatePivotValueColumn } from "./nucleus_explain_action";
import { formatInventoryMatrixDisplayValue } from "./inventory_matrix_display";

export type CommonRenderedCellContextV1 = Readonly<{
  version: "CommonRenderedCellContext.v1";
  surfaceId: "inventory-health";
  variant: InventoryHealthVariant;
  receiptId: string;
  cellKind: "matrix_cell";
  selector: Readonly<{
    segmentation: string;
    measure: string;
    measureRank: number;
    columnKey: string;
  }>;
  display: Readonly<{
    rawValue: string;
    rule: "inventory_matrix_v1";
  }>;
}>;

export type CellSelectorScalarV1 =
  | Readonly<{ kind: "string"; value: string }>
  | Readonly<{ kind: "number"; value: string }>
  | Readonly<{ kind: "boolean"; value: boolean }>
  | Readonly<{ kind: "null" }>;

export type CommonRenderedCellContextV2 = Readonly<{
  version: "CommonRenderedCellContext.v2";
  surfaceId: "demand" | "supply";
  receiptId: string;
  cellKind: "date_pivot_cell";
  selector: Readonly<{
    dimensions: readonly Readonly<{
      field: string;
      value: CellSelectorScalarV1;
    }>[];
    measure: string;
    dateField: string;
    columnKey: string;
  }>;
  display: Readonly<{
    rawValue: string;
    rule: "generic_date_pivot_v1";
  }>;
}>;

const RECEIPT_ID = /^rvr1_[a-f0-9]{64}$/;
const RECEIPT_ID_V2 = /^rvr2_[a-f0-9]{64}$/;
const SAFE_FIELD = /^[A-Za-z_][A-Za-z0-9_]{0,127}$/;
const BOUNDED_INTEGER = /^-?(?:0|[1-9][0-9]{0,37})$/;
const MAX_DATE_PIVOT_DIMENSIONS = 32;

const utf8ByteLength = (value: string): number => {
  let bytes = 0;
  for (let index = 0; index < value.length; index += 1) {
    const codeUnit = value.charCodeAt(index);
    if (codeUnit < 0x80) bytes += 1;
    else if (codeUnit < 0x800) bytes += 2;
    else if (
      codeUnit >= 0xd800 &&
      codeUnit <= 0xdbff &&
      index + 1 < value.length &&
      value.charCodeAt(index + 1) >= 0xdc00 &&
      value.charCodeAt(index + 1) <= 0xdfff
    ) {
      bytes += 4;
      index += 1;
    } else bytes += 3;
  }
  return bytes;
};

const boundedText = (value: unknown, maxBytes: number): string | undefined =>
  typeof value === "string" &&
  value.length > 0 &&
  value === value.trim() &&
  utf8ByteLength(value) <= maxBytes &&
  !/\p{Cc}/u.test(value)
    ? value
    : undefined;

const isVariant = (value: unknown): value is InventoryHealthVariant =>
  value === "standard" ||
  value === "with_transfers" ||
  value === "for_hubs" ||
  value === "raw";

const canonicalRawValue = (value: unknown): string | undefined => {
  if (
    (typeof value !== "number" && typeof value !== "string") ||
    (typeof value === "string" &&
      (value.length === 0 || value !== value.trim()))
  ) {
    return undefined;
  }
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return undefined;
  return Object.is(numeric, -0) ? "0" : String(numeric);
};

const canonicalColumnKey = (value: unknown): string | undefined => {
  return typeof value === "string" &&
    isNucleusExplainDatePivotValueColumn(value)
    ? value
    : undefined;
};

const canonicalMeasureRank = (value: unknown): number | undefined =>
  Number.isSafeInteger(value) &&
  (value as number) >= 1 &&
  (value as number) <= 5_000_000
    ? (value as number)
    : undefined;

export function createRenderedCellContext(
  input: Readonly<{
    variant: unknown;
    receiptId: unknown;
    columnKey: unknown;
    row: Readonly<Record<string, unknown>>;
    rawValue: unknown;
    selectedMeasure: unknown;
  }>,
): CommonRenderedCellContextV1 | undefined {
  if (
    !isVariant(input.variant) ||
    typeof input.receiptId !== "string" ||
    !RECEIPT_ID.test(input.receiptId) ||
    !input.row ||
    typeof input.row !== "object" ||
    Array.isArray(input.row)
  ) {
    return undefined;
  }
  const columnKey = canonicalColumnKey(input.columnKey);
  const rawValue = canonicalRawValue(input.rawValue);
  const measureRank = canonicalMeasureRank(input.row.measureRank);
  const rowType = input.row.type;
  const segmentation = boundedText(
    rowType === "detail"
      ? input.row.parentSegmentation
      : rowType === "group"
        ? input.row.segmentation
        : undefined,
    2_048,
  );
  const measure = boundedText(
    rowType === "detail"
      ? input.row.measure
      : rowType === "group"
        ? (input.row.measure ?? input.selectedMeasure)
        : undefined,
    128,
  );
  if (
    !columnKey ||
    rawValue === undefined ||
    !segmentation ||
    !measure ||
    measureRank === undefined
  ) {
    return undefined;
  }
  return Object.freeze({
    version: "CommonRenderedCellContext.v1",
    surfaceId: "inventory-health",
    variant: input.variant,
    receiptId: input.receiptId,
    cellKind: "matrix_cell",
    selector: Object.freeze({ segmentation, measure, measureRank, columnKey }),
    display: Object.freeze({ rawValue, rule: "inventory_matrix_v1" }),
  });
}

export function isSupportedRenderedCellContext(
  context: CommonRenderedCellContextV1,
): boolean {
  return Boolean(
    context.version === "CommonRenderedCellContext.v1" &&
      context.surfaceId === "inventory-health" &&
      isVariant(context.variant) &&
      RECEIPT_ID.test(context.receiptId) &&
      context.cellKind === "matrix_cell" &&
      boundedText(context.selector.segmentation, 2_048) &&
      boundedText(context.selector.measure, 128) &&
      canonicalMeasureRank(context.selector.measureRank) ===
        context.selector.measureRank &&
      canonicalColumnKey(context.selector.columnKey) &&
      canonicalRawValue(context.display.rawValue) ===
        context.display.rawValue &&
      context.display.rule === "inventory_matrix_v1",
  );
}

export function renderedCellPrompt(
  context: CommonRenderedCellContextV1,
  dateHierarchy: unknown,
): string {
  const measure = context.selector.measure.replaceAll("_", " ");
  const displayedValue = formatInventoryMatrixDisplayValue(
    context.display.rawValue,
    context.selector.measure,
  );
  let period = "across the selected date range";
  if (context.selector.columnKey !== "TOTAL") {
    const date = new Date(Number(context.selector.columnKey));
    const normalizedHierarchy =
      typeof dateHierarchy === "string" ? dateHierarchy.toLowerCase() : "";
    const fullDate = new Intl.DateTimeFormat("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
      timeZone: "UTC",
    }).format(date);
    if (normalizedHierarchy.endsWith("month")) {
      const month = new Intl.DateTimeFormat("en-US", {
        month: "long",
        year: "numeric",
        timeZone: "UTC",
      }).format(date);
      period = `in ${month}`;
    } else if (normalizedHierarchy.endsWith("week")) {
      period = `during the week beginning ${fullDate}`;
    } else {
      period = `on ${fullDate}`;
    }
  }
  return calculationFirstCellPrompt(
    `${measure} for ${context.selector.segmentation} ${period}, displayed value ${displayedValue}`,
  );
}

const exactKeys = (value: object, keys: readonly string[]): boolean => {
  const actual = Object.keys(value);
  return (
    actual.length === keys.length && keys.every((key) => actual.includes(key))
  );
};

const canonicalDatePivotColumnKey = (value: unknown): string | undefined => {
  return canonicalColumnKey(value);
};

const canonicalSelectorNumber = (value: number): string | undefined => {
  if (!Number.isFinite(value)) return undefined;
  const canonical = Object.is(value, -0) ? "0" : String(value);
  return canonical.length <= 64 ? canonical : undefined;
};

const canonicalSelectorBigInt = (value: bigint): string | undefined => {
  const canonical = value.toString();
  return BOUNDED_INTEGER.test(canonical) ? canonical : undefined;
};

const isCanonicalSelectorNumberText = (value: string): boolean =>
  (BOUNDED_INTEGER.test(value) && value !== "-0") ||
  canonicalSelectorNumber(Number(value)) === value;

const selectorScalar = (value: unknown): CellSelectorScalarV1 | undefined => {
  if (value === null) return Object.freeze({ kind: "null" });
  if (typeof value === "boolean") {
    return Object.freeze({ kind: "boolean", value });
  }
  if (typeof value === "number") {
    const canonical = canonicalSelectorNumber(value);
    return canonical === undefined
      ? undefined
      : Object.freeze({ kind: "number", value: canonical });
  }
  if (typeof value === "bigint") {
    const canonical = canonicalSelectorBigInt(value);
    return canonical === undefined
      ? undefined
      : Object.freeze({ kind: "number", value: canonical });
  }
  if (
    typeof value === "string" &&
    utf8ByteLength(value) <= 512 &&
    !/\p{Cc}/u.test(value)
  ) {
    return Object.freeze({ kind: "string", value });
  }
  return undefined;
};

const isSelectorScalar = (value: unknown): value is CellSelectorScalarV1 => {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  const candidate = value as Record<string, unknown>;
  if (candidate.kind === "null") return exactKeys(candidate, ["kind"]);
  if (candidate.kind === "boolean") {
    return (
      exactKeys(candidate, ["kind", "value"]) &&
      typeof candidate.value === "boolean"
    );
  }
  if (candidate.kind === "number") {
    return (
      exactKeys(candidate, ["kind", "value"]) &&
      typeof candidate.value === "string" &&
      isCanonicalSelectorNumberText(candidate.value)
    );
  }
  return (
    candidate.kind === "string" &&
    exactKeys(candidate, ["kind", "value"]) &&
    typeof candidate.value === "string" &&
    utf8ByteLength(candidate.value) <= 512 &&
    !/\p{Cc}/u.test(candidate.value)
  );
};

const safeField = (value: unknown): value is string =>
  typeof value === "string" && SAFE_FIELD.test(value);

export function createRenderedDatePivotCellContext(
  input: Readonly<{
    surfaceId: unknown;
    receiptId: unknown;
    columnKey: unknown;
    segments: readonly string[] | undefined;
    row: Readonly<Record<string, unknown>>;
    measure: unknown;
    dateField: unknown;
    rawValue: unknown;
  }>,
): CommonRenderedCellContextV2 | undefined {
  if (
    (input.surfaceId !== "demand" && input.surfaceId !== "supply") ||
    typeof input.receiptId !== "string" ||
    !RECEIPT_ID_V2.test(input.receiptId) ||
    !Array.isArray(input.segments) ||
    input.segments.length > MAX_DATE_PIVOT_DIMENSIONS ||
    !input.row ||
    typeof input.row !== "object" ||
    Array.isArray(input.row) ||
    !safeField(input.measure) ||
    !safeField(input.dateField)
  ) {
    return undefined;
  }
  const columnKey = canonicalDatePivotColumnKey(input.columnKey);
  const rawValue = canonicalRawValue(input.rawValue);
  if (!columnKey || rawValue === undefined) return undefined;

  const dimensions: Array<
    Readonly<{ field: string; value: CellSelectorScalarV1 }>
  > = [];
  const fields = new Set<string>();
  if (input.segments.length === 0) {
    dimensions.push(
      Object.freeze({
        field: "TOTAL",
        value: Object.freeze({ kind: "string", value: "TOTAL" }),
      }),
    );
  } else {
    for (const candidate of input.segments) {
      if (
        !safeField(candidate) ||
        candidate === "TOTAL" ||
        fields.has(candidate)
      ) {
        return undefined;
      }
      const value = selectorScalar(input.row[candidate]);
      if (!value) return undefined;
      fields.add(candidate);
      dimensions.push(Object.freeze({ field: candidate, value }));
    }
  }

  return Object.freeze({
    version: "CommonRenderedCellContext.v2",
    surfaceId: input.surfaceId,
    receiptId: input.receiptId,
    cellKind: "date_pivot_cell",
    selector: Object.freeze({
      dimensions: Object.freeze(dimensions),
      measure: input.measure,
      dateField: input.dateField,
      columnKey,
    }),
    display: Object.freeze({ rawValue, rule: "generic_date_pivot_v1" }),
  });
}

export function isSupportedRenderedDatePivotCellContext(
  context: CommonRenderedCellContextV2,
): boolean {
  if (
    !context ||
    typeof context !== "object" ||
    !exactKeys(context, [
      "version",
      "surfaceId",
      "receiptId",
      "cellKind",
      "selector",
      "display",
    ]) ||
    context.version !== "CommonRenderedCellContext.v2" ||
    (context.surfaceId !== "demand" && context.surfaceId !== "supply") ||
    !RECEIPT_ID_V2.test(context.receiptId) ||
    context.cellKind !== "date_pivot_cell" ||
    !context.selector ||
    !exactKeys(context.selector, [
      "dimensions",
      "measure",
      "dateField",
      "columnKey",
    ]) ||
    !safeField(context.selector.measure) ||
    !safeField(context.selector.dateField) ||
    canonicalDatePivotColumnKey(context.selector.columnKey) !==
      context.selector.columnKey ||
    !Array.isArray(context.selector.dimensions) ||
    context.selector.dimensions.length === 0 ||
    context.selector.dimensions.length > MAX_DATE_PIVOT_DIMENSIONS ||
    !context.display ||
    !exactKeys(context.display, ["rawValue", "rule"]) ||
    canonicalRawValue(context.display.rawValue) !== context.display.rawValue ||
    context.display.rule !== "generic_date_pivot_v1"
  ) {
    return false;
  }

  const fields = new Set<string>();
  for (const dimension of context.selector.dimensions) {
    if (
      !dimension ||
      typeof dimension !== "object" ||
      !exactKeys(dimension, ["field", "value"]) ||
      !safeField(dimension.field) ||
      fields.has(dimension.field) ||
      !isSelectorScalar(dimension.value)
    ) {
      return false;
    }
    fields.add(dimension.field);
  }
  const total = context.selector.dimensions[0];
  if (total.field === "TOTAL") {
    return (
      context.selector.dimensions.length === 1 &&
      total.value.kind === "string" &&
      total.value.value === "TOTAL"
    );
  }
  return !fields.has("TOTAL");
}

export function renderedDatePivotCellPrompt(
  context: CommonRenderedCellContextV2,
  viewContext?: CommonViewContextV1,
): string {
  const matchingViewContext = matchesDatePivotViewContext(context, viewContext)
    ? viewContext
    : undefined;
  const totalRow =
    context.selector.dimensions.length === 1 &&
    context.selector.dimensions[0].field === "TOTAL";
  const measure = humanizeField(context.selector.measure);
  const dimensions = totalRow
    ? ""
    : dimensionPrompt(context.selector.dimensions);
  const period = datePivotPeriod(context, matchingViewContext);
  const displayedValue = formatDatePivotDisplayValue(context);
  const prompt = calculationFirstCellPrompt(
    `the ${totalRow ? "total " : ""}${context.surfaceId} ${measure}${dimensions} ${period}, displayed value ${displayedValue}`,
  );
  return `${prompt}${activeFilterPrompt(matchingViewContext)}`;
}

const HUMAN_FIELD_TOKENS: Readonly<Record<string, string>> = Object.freeze({
  id: "ID",
  po: "PO",
  sku: "SKU",
  upc: "UPC",
});

const PLATFORM_INTERNAL_FIELDS: ReadonlySet<string> = new Set([
  "artifact_key",
  "input_id",
  "organization_id",
  "receipt_id",
  "request_id",
  "run_attempt_id",
  "run_id",
  "s3_key",
  "settings_name_and_version",
  "source_key",
  "tool_id",
  "trace_id",
  "workflow_id",
]);

const isPlatformInternalField = (field: string): boolean =>
  PLATFORM_INTERNAL_FIELDS.has(field.toLowerCase());

const INTERNAL_IDENTIFIER_VALUE =
  /^(?:rvr[12]_[a-f0-9]{64}|arn:aws(?:-[a-z]+)*:|s3:\/\/)/i;
const MAX_VISIBLE_DIMENSIONS = 8;
const MAX_VISIBLE_DIMENSION_BYTES = 2 * 1024;

const humanizeField = (field: string): string =>
  field
    .split("_")
    .map((token) => HUMAN_FIELD_TOKENS[token.toLowerCase()] ?? token)
    .join(" ");

const formatSelectorScalar = (value: CellSelectorScalarV1): string => {
  if (value.kind === "null") return "blank";
  if (value.kind === "boolean") return value.value ? "true" : "false";
  if (value.kind === "number") return value.value;
  return INTERNAL_IDENTIFIER_VALUE.test(value.value)
    ? "selected value"
    : JSON.stringify(value.value);
};

const dimensionPrompt = (
  dimensions: CommonRenderedCellContextV2["selector"]["dimensions"],
): string => {
  const visible = dimensions.filter(
    (dimension) => !isPlatformInternalField(dimension.field),
  );
  if (visible.length === 0) return " for the selected row";
  const detail = visible
    .map(
      (dimension) =>
        `${humanizeField(dimension.field)} ${formatSelectorScalar(
          dimension.value,
        )}`,
    )
    .join(" and ");
  if (
    visible.length > MAX_VISIBLE_DIMENSIONS ||
    utf8ByteLength(detail) > MAX_VISIBLE_DIMENSION_BYTES
  ) {
    return ` for the selected row (${visible.length} selected dimensions; exact values are included with this request)`;
  }
  return ` for ${detail}`;
};

const fullUtcDate = (date: Date): string =>
  new Intl.DateTimeFormat("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  }).format(date);

const monthUtcDate = (date: Date): string =>
  new Intl.DateTimeFormat("en-US", {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  }).format(date);

const datePivotRange = (
  viewContext: CommonViewContextV1 | undefined,
): string => {
  const min = viewContext?.dateRange.min
    ? new Date(viewContext.dateRange.min)
    : undefined;
  const max = viewContext?.dateRange.max
    ? new Date(viewContext.dateRange.max)
    : undefined;
  if (min && max) return `from ${fullUtcDate(min)} through ${fullUtcDate(max)}`;
  if (min) return `from ${fullUtcDate(min)} onward`;
  if (max) return `through ${fullUtcDate(max)}`;
  return "across all dates in the rendered view";
};

const datePivotPeriod = (
  context: CommonRenderedCellContextV2,
  viewContext: CommonViewContextV1 | undefined,
): string => {
  if (context.selector.columnKey === "TOTAL") {
    return datePivotRange(viewContext);
  }
  const date = new Date(Number(context.selector.columnKey));
  const dateField = context.selector.dateField.toLowerCase();
  if (dateField.endsWith("_week") || dateField === "week") {
    return `during the week beginning ${fullUtcDate(date)}`;
  }
  if (dateField.endsWith("_month") || dateField === "month") {
    return `in ${monthUtcDate(date)}`;
  }
  if (dateField.endsWith("_quarter") || dateField === "quarter") {
    return `in Q${Math.floor(date.getUTCMonth() / 3) + 1} ${date.getUTCFullYear()}`;
  }
  if (dateField.endsWith("_year") || dateField === "year") {
    return `in ${date.getUTCFullYear()}`;
  }
  return `on ${fullUtcDate(date)}`;
};

const formatDatePivotDisplayValue = (
  context: CommonRenderedCellContextV2,
): string => {
  const numeric = Number(context.display.rawValue);
  if (isCurrencyField(context.selector.measure)) {
    return formatCurrency(numeric, context.selector.measure);
  }
  const value = Math.round(numeric).toLocaleString("en-US");
  if (context.selector.measure === "quantity") return `${value} units`;
  if (context.selector.measure === "weight_in_pounds") {
    return `${value} pounds`;
  }
  if (context.selector.measure === "eaches") return `${value} eaches`;
  if (context.selector.measure === "pallets") return `${value} pallets`;
  return value;
};

const matchesDatePivotViewContext = (
  context: CommonRenderedCellContextV2,
  viewContext: CommonViewContextV1 | undefined,
): viewContext is CommonViewContextV1 => {
  if (
    !viewContext ||
    viewContext.version !== "CommonViewContext.v1" ||
    viewContext.trust !== "untrusted_ui_selection" ||
    viewContext.surfaceId !== context.surfaceId ||
    viewContext.measure !== context.selector.measure ||
    viewContext.dateHierarchy !== context.selector.dateField
  ) {
    return false;
  }
  const dimensions = context.selector.dimensions;
  if (dimensions.length === 1 && dimensions[0].field === "TOTAL") {
    return viewContext.segments.length === 0;
  }
  return (
    dimensions.length === viewContext.segments.length &&
    dimensions.every(
      (dimension, index) => dimension.field === viewContext.segments[index],
    )
  );
};

const filterValues = (values: readonly string[]): string =>
  values.map((value) => JSON.stringify(value)).join(" or ");

const customerFilter = (
  filter: CommonViewContextV1["filters"][number],
): string => {
  const field = humanizeField(filter.field);
  const values = filterValues(filter.values);
  if (filter.operator === "in") return `${field} is ${values}`;
  if (filter.operator === "not in") return `${field} excludes ${values}`;
  if (filter.operator === "contains") return `${field} contains ${values}`;
  if (filter.operator === "not contains") {
    return `${field} does not contain ${values}`;
  }
  return `${field} uses the ${JSON.stringify(filter.operator)} filter with ${values}`;
};

const activeFilterPrompt = (
  viewContext: CommonViewContextV1 | undefined,
): string => {
  if (!viewContext) return "";
  const filters = [...viewContext.filters, ...viewContext.postFilters];
  if (filters.length === 0) return "";
  const valueCount = filters.reduce(
    (total, filter) => total + filter.values.length,
    0,
  );
  const includesInternalIdentifier = filters.some(
    (filter) =>
      isPlatformInternalField(filter.field) ||
      filter.values.some((value) => INTERNAL_IDENTIFIER_VALUE.test(value)),
  );
  if (filters.length > 6 || valueCount > 12 || includesInternalIdentifier) {
    return " Additional active filters are included with this request.";
  }
  const detail = filters.map(customerFilter).join("; ");
  return detail.length <= 1_024
    ? ` Active filters: ${detail}.`
    : " Additional active filters are included with this request.";
};
